<script lang="ts">
import { defineComponent, onMounted, watch, defineAsyncComponent, computed, h } from 'vue'
import { usePaymentForm } from '~/shared/use-payment-form'
import {
  isSensePassWidgetQueryParam,
  normalizeSensePassWidgetFormIds,
  resolveClientRuntime,
  resolveCreditCardWidgetUrl,
  resolveSensePassWidgetVersionFromQuery
} from '~/shared/runtime-environment'
import { useRoute } from 'vue-router'
import { useRuntimeConfig } from 'nuxt/app'
import LogRocket from 'logrocket'
import { queryParams } from '~/shared/query-params'
import {
  findTemplateModuleKey,
  resolveThankYouTemplateId,
  thankYouTemplateUsesMessage
} from '~/shared/thank-you-template'

// All components in payment-form-templates are auto-imported by Nuxt

export default defineComponent({
  name: 'PaymentFormContainer',
  props: {
    id: { type: String, default: undefined },
    testMode: { type: Boolean, default: false },
    subCampaign: { type: String, default: undefined },
    campaign: { type: String, default: undefined },
    callback: { type: String, default: undefined },
    checkText: { type: String, default: undefined },
    gateway: { type: String, default: undefined },
    noCharge: { type: Boolean, default: false }
  },
  setup(props) {
    const runtimeConfig = useRuntimeConfig()
    const sensepassWidgetFormIds = normalizeSensePassWidgetFormIds(
      runtimeConfig.public.sensepassWidgetFormIds
    )
    const params = queryParams()
    const sensepassWidgetVersion = resolveSensePassWidgetVersionFromQuery(params)
    const currentScriptSrc = typeof document !== 'undefined'
      ? document.currentScript?.src
      : undefined
    const scriptUrl = currentScriptSrc && typeof window !== 'undefined'
      ? new URL(currentScriptSrc, window.location.href)
      : null
    const embedded = !!scriptUrl && typeof window !== 'undefined'
      ? scriptUrl.hostname !== window.location.hostname || scriptUrl.port !== window.location.port
      : false

    // when embedding from the CDN, make sure lazy loading loads from the correct place
    if (embedded && currentScriptSrc) {
      //@ts-ignore
      __webpack_public_path__ = currentScriptSrc.split('/').slice(0, -1).join('/') + '/'
    }

    const runtime = resolveClientRuntime({
      locationOrigin: typeof window !== 'undefined' ? window.location.origin : undefined,
      scriptSrc: currentScriptSrc,
      explicitTestMode: props.testMode
    })
    const testMode = runtime.testMode
    const apiUrl = runtime.apiUrl

    if (typeof window !== 'undefined') {
      //@ts-ignore
      window.isProduction = runtime.isProduction

      const nuxt = ((window as any).__NUXT__ = (window as any).__NUXT__ || {})
      nuxt.config = nuxt.config || { public: {} }
      nuxt.config.public = nuxt.config.public || {}
      nuxt.config.public.testMode = testMode
      nuxt.config.public.api = apiUrl
      nuxt.config.public.apiUrl = apiUrl
      ;(window as any).__PAYMENT_FORM_API_URL__ = apiUrl
    }

    onMounted(() => {
      // Load GTM with runtime-resolved container ID
      ;(function(w: any, d: Document, s: string, l: string, i: string) {
        w[l] = w[l] || []
        w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
        const f = d.getElementsByTagName(s)[0]
        const j = d.createElement(s) as HTMLScriptElement
        const dl = l !== 'dataLayer' ? '&l=' + l : ''
        j.async = true
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
        f.parentNode?.insertBefore(j, f)
      })(window, document, 'script', 'dataLayer', runtime.gtmId)

      //@ts-ignore
      if (window.isProduction === true)
        LogRocket.init('orthodox-union/donation-forms')

      fetch('https://payment.ouapis.org/api/payment/gateways')

      setInterval(() => {
        fetch('https://payment.ouapis.org/api/payment/gateways')
      }, 4000 * 60)

      const script = document.createElement('script')
      script.defer = true
      script.type = 'module'
      script.src = resolveCreditCardWidgetUrl(
        testMode,
        props.id,
        sensepassWidgetFormIds,
        sensepassWidgetVersion
      )

      document.body.appendChild(script)
    })

    const paymentForm = usePaymentForm(props.id, { testMode, noCharge: props.noCharge || false })

    if (!paymentForm) {
      throw new Error('PaymentForm context not available')
    }

    const { submitted, settings, setValue, actorRef } = paymentForm

    // Debug: watch submitted state
    watch(submitted, (newVal) => {
      console.log('[PaymentFormContainer] submitted changed:', newVal)
      console.log('[PaymentFormContainer] current state:', actorRef.getSnapshot().value)
      console.log('[PaymentFormContainer] redirect context:', actorRef.getSnapshot().context.redirect)
    }, { immediate: true })

    // Collect all values to apply once machine is initialized
    const valuesToApply: Record<string, any> = {}

    if (props.callback) {
      valuesToApply.callback = props.callback
    }

    const booleanFields = ['handleProcessing', 'signup', 'toc']
    for (const key in params) {
      if (isSensePassWidgetQueryParam(key)) continue

      // Only apply non-empty, non-undefined values
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        let value: any = params[key]
        // Currency options are uppercase (USD, ILS, CAD) — normalize the param
        if (key === 'currency') {
          value = value.toUpperCase()
        }
        // Coerce boolean-like strings from URL params for known boolean fields
        if (booleanFields.includes(key)) {
          value = value === 'true'
        }
        valuesToApply[key] = value
      }
    }

    // Wait for machine to enter 'editing' state before applying any values
    // Using XState v5 subscribe API - access actorRef directly from paymentForm to avoid stale reference
    onMounted(() => {
      console.log('[PaymentFormContainer] onMounted - actorRef available:', !!paymentForm.actorRef)

      // ActorRef is immediately available, set up subscription
      if (paymentForm.actorRef) {
        console.log('[PaymentFormContainer] ActorRef available, setting up subscription')
        let hasAppliedInitialValues = false
        const subscription = paymentForm.actorRef.subscribe((snapshot) => {
          console.log('[PaymentFormContainer] Subscription callback - state:', snapshot.value, 'hasApplied:', hasAppliedInitialValues)
          if (snapshot.value === 'editing' && !hasAppliedInitialValues) {
            console.log('[PaymentFormContainer] Machine entered editing state, applying initial values')
            hasAppliedInitialValues = true

            // Apply all collected values
            for (const key in valuesToApply) {
              // Skip empty keys or undefined values
              if (key && valuesToApply[key] !== undefined && valuesToApply[key] !== null && valuesToApply[key] !== '') {
                console.log('[PaymentFormContainer] Applying:', key, '=', valuesToApply[key])
                // @ts-ignore
                setValue(key, valuesToApply[key])
              }
            }

            // Set up gateway watcher if needed
            if (props.gateway) {
              watch(() => props.gateway, () => {
                setValue('gateway', props.gateway!)
              }, { immediate: true })
            }

            subscription.unsubscribe()
          }
        })
      }
    })

    // Use import.meta.glob to get all payment form templates at build time
    // This creates a record of all .vue files in payment-form-templates and pledge-form-templates
    const paymentFormModules = import.meta.glob(['../payment-form-templates/**/*.vue', '../pledge-form-templates/**/*.vue'])
    const thankYouTemplateModules = import.meta.glob('../thank-you-template/**/*.vue')

    console.log('[PaymentFormContainer] Loaded paymentFormModules:', Object.keys(paymentFormModules).length, 'modules')
    if (Object.keys(paymentFormModules).length === 0) {
      console.error('[PaymentFormContainer] NO MODULES LOADED! This will prevent any forms from rendering.')
    }

    console.log(
      '[PaymentFormContainer] Loaded thankYouTemplateModules:',
      Object.keys(thankYouTemplateModules).length,
      'modules'
    )

    // Get the payment form component using the pre-loaded modules
    const paymentFormComponent = computed(() => {
      console.log('[PaymentFormContainer] Computing paymentFormComponent', {
        propsId: props.id,
        settingsTemplate: settings?.template,
        totalModules: Object.keys(paymentFormModules).length
      })

      if (!props.id) {
        console.log('[PaymentFormContainer] No props.id, returning null')
        return null
      }

      // Use template override from settings if available, otherwise use form ID
      const templateId = settings?.template || props.id
      console.log('[PaymentFormContainer] Looking for template:', templateId)

      // Search through all glob results for a matching form ID
      // This automatically handles any directory structure without hardcoded paths
      const matchingPath = Object.keys(paymentFormModules).find(path => {
        // Extract filename without extension
        const filename = path.split('/').pop()?.replace('.vue', '')
        return filename === templateId
      })

      if (matchingPath) {
        console.log('[PaymentFormContainer] Found matching path:', matchingPath)
        return defineAsyncComponent(paymentFormModules[matchingPath] as any)
      }

      console.warn(`Payment form not found: ${templateId} (form ID: ${props.id})`, 'Available:', Object.keys(paymentFormModules))
      // Return a placeholder component
      return defineAsyncComponent(() => Promise.resolve({
        render: () => h('div', {
          style: {
            padding: '20px',
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px'
          }
        }, [
          h('h3', 'Form Not Available'),
          h('p', `Payment form template "${templateId}" not found for form ID "${props.id}".`)
        ])
      }))
    })

    const route = useRoute()
    const isSubmitted = computed(() => {
      const result = route.query.thankyou !== undefined || submitted.value
      console.log('[PaymentFormContainer] isSubmitted computed:', result, 'query:', route.query, 'submitted:', submitted.value)
      return result
    })

    const thankYouTemplateId = computed(() => {
      const resolved = resolveThankYouTemplateId(
        props.id,
        settings,
        Object.keys(thankYouTemplateModules)
      )

      console.log('[PaymentFormContainer] thankYouTemplateId:', resolved)
      console.log('[PaymentFormContainer] settings.message:', settings?.message)

      return resolved
    })

    const thankYouComponent = computed(() => {
      const moduleKey = findTemplateModuleKey(
        thankYouTemplateModules,
        thankYouTemplateId.value
      )

      if (!moduleKey) {
        console.warn(
          '[PaymentFormContainer] Thank you template not found:',
          thankYouTemplateId.value
        )
        return null
      }

      return defineAsyncComponent(thankYouTemplateModules[moduleKey] as any)
    })

    // Debug: log rendering decisions
    watch([isSubmitted, () => paymentFormComponent.value], ([submitted, component]) => {
      console.log('[PaymentFormContainer] Render decision:', {
        isSubmitted: submitted,
        hasPaymentFormComponent: !!component,
        componentName: component?.name || component?.__name || 'unknown'
      })
    }, { immediate: true })

    // Use render function instead of template for web component compatibility
    return () => {
      console.log('[PaymentFormContainer] Render function executing', {
        isSubmittedValue: isSubmitted.value,
        hasPaymentFormComponent: !!paymentFormComponent.value
      })

      if (isSubmitted.value) {
        return h('div', {}, [
          thankYouComponent.value
            ? h(
              thankYouComponent.value,
              thankYouTemplateUsesMessage(thankYouTemplateId.value)
                ? { message: settings?.message }
                : {}
            )
            : null
        ])
      } else {
        // Render payment form
        return h('div', {}, [
          paymentFormComponent.value ? h(paymentFormComponent.value, {
            subCampaign: props.subCampaign,
            campaign: props.campaign,
            checkText: props.checkText
          }) : null
        ])
      }
    }
  }
})
</script>
